/**
 * Confirmation Dialog Composable
 *
 * Provides confirmation dialog functionality
 */
import { ref, readonly } from 'vue';

export interface ConfirmOptions {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  type?: 'danger' | 'warning' | 'info';
}

interface ConfirmState extends ConfirmOptions {
  isOpen: boolean;
  resolve?: (value: boolean) => void;
}

const confirmState = ref<ConfirmState>({
  isOpen: false,
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel',
  type: 'info',
});

export function useConfirm() {
  function confirm(options: ConfirmOptions): Promise<boolean> {
    return new Promise((resolve) => {
      confirmState.value = {
        ...options,
        isOpen: true,
        confirmText: options.confirmText || 'Confirm',
        cancelText: options.cancelText || 'Cancel',
        type: options.type || 'info',
        resolve,
      };
    });
  }

  function handleConfirm() {
    confirmState.value.resolve?.(true);
    confirmState.value.isOpen = false;
  }

  function handleCancel() {
    confirmState.value.resolve?.(false);
    confirmState.value.isOpen = false;
  }

  return {
    confirmState: readonly(confirmState),
    confirm,
    handleConfirm,
    handleCancel,
  };
}
